import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ADMIN_AUTH_EXPIRED_EVENT, adminFetch } from '../services/adminApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const storedToken = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAuthState();
    };

    const handleStorageChange = (event) => {
      if (event.key === 'adminToken' && !event.newValue) {
        clearAuthState();
      }
    };

    window.addEventListener(ADMIN_AUTH_EXPIRED_EVENT, handleAuthExpired);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(ADMIN_AUTH_EXPIRED_EVENT, handleAuthExpired);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [clearAuthState]);

  const login = async (username, password) => {
    try {
      const { adminAuth } = await import('../services/adminApi');
      const response = await adminAuth.login(username, password);
      const data = response.data;

      if (response.status === 200) {
        localStorage.setItem('adminToken', data.data.token);
        localStorage.setItem('adminUser', JSON.stringify({ username }));
        setToken(data.data.token);
        setUser({ username });
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = useCallback(() => {
    clearAuthState();
  }, [clearAuthState]);

  const verifyToken = useCallback(async () => {
    if (!token) return false;
    
    try {
      // Use a simple token verification endpoint instead of loading boards
      const response = await adminFetch('/admin/verify-token', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      logout();
      return false;
    }
  }, [logout, token]);

  const value = {
    isAuthenticated,
    token,
    user,
    loading,
    login,
    logout,
    verifyToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
